import type { InvoiceListFields } from 'redux/modules/invoice.types';
import type { Account, Currency } from 'types/account';
import type { Cost, Duration } from 'types/units';
import type { ProjectBase } from './project';
import type { LightUser } from './user';

interface InvoiceLine {
  description: string;
  quantity: Duration;
  cost: Cost;
}

export enum InvoiceBy {
  Client = 'client',
  Project = 'project',
}

export enum ItemGrouping {
  Projects = 'projects',
  Labels = 'labels',
  Users = 'users',
  Teams = 'teams',
}

export enum InvoiceStatus {
  Incomplete = 'incomplete',
  LocalDraft = 'local_draft',
  Draft = 'draft',
  SentToCustomer = 'sent_to_customer',
  Void = 'void',
  Paid = 'paid',
}

export type InvoiceStatusFilterKeys =
  | 'all'
  | 'local_draft'
  | 'draft'
  | 'sent_to_customer'
  | 'paid'
  | 'void';

interface GenericInvoice {
  id: number;
  account?: Pick<Account, 'id' | 'name' | 'color' | 'currency' | 'logo'>;
  created_at: string;
  updated_at: string;
  invoice_by: InvoiceBy;
  list_item_grouping: ItemGrouping;
}

interface InvoiceRequestFilters {
  project_ids?: Array<number>;
  client_ids?: Array<number>;
}

export interface InvoiceRequestBody
  extends Pick<RawInvoice, 'name' | 'description' | 'date' | 'from' | 'to'> {
  invoice_by: InvoiceBy;
  list_item_grouping: ItemGrouping;
  connection_id?: number;
  filters: InvoiceRequestFilters;
}

export interface ExternalReference {
  connection_id: number;
  id: number;
  provider_id: number | string; // id in external service
  provider_type: string; // e.g. "Quickbooks"
  updated_at: string;
  url: string | null;
}

export interface RawInvoice extends GenericInvoice {
  description: string;
  external_references: Array<ExternalReference>;
  external_total_cost: Cost;
  name: string;
  user: Pick<LightUser, 'id' | 'email' | 'name' | 'avatar' | 'updated_at'>;
  date: string;
  due_date: string | null;
  from: string | null;
  to: string | null;
  paid: boolean;
  total_cost: Cost;
  total_hours: Duration | number;
  lines: Array<InvoiceLine>;
  filters: InvoiceRequestFilters;
  status: InvoiceStatus;
  project: ProjectBase | null;
  deleted: boolean;
}

export interface InvoiceDraft extends RawInvoice {
  isAwaitingSync?: boolean;
  connectionId?: number;
}

export interface InvoiceCreationResponse {
  creating_invoices: number;
  invoice_ids: Array<number>;
  skipped_client_ids: Array<InvoiceSkippedItemDetails>;
  skipped_project_ids: Array<InvoiceSkippedItemDetails>;
  error: string;
}

export interface InvoiceSkippedItemDetails {
  id: number;
  error: string;
}

export interface InvoicePreviewProjectDetails {
  project_id: number;
  hours_count: number;
  total_cost: Cost;
  currency?: Currency;
}

export interface InvoicePreviewCreationResponse
  extends Pick<InvoiceCreationResponse, 'skipped_client_ids' | 'skipped_project_ids' | 'error'> {
  invoices_count: number;
  project_ids: Array<InvoicePreviewProjectDetails>;
}

export enum InvoiceMethods {
  Automatic = 'automatic',
  Manual = 'manual',
}

export enum InvoiceDates {
  FirstOfMonth = 'first_of_month',
  LastOfMonth = 'last_of_month',
}

export interface RawInvoiceRules extends GenericInvoice {
  invoice_method: InvoiceMethods;
  standard_invoice_date: InvoiceDates;
}

export interface UpdateInvoiceRulesRequest {
  invoice_method: InvoiceMethods;
  standard_invoice_date: InvoiceDates;
  invoice_by: InvoiceBy;
  list_item_grouping: ItemGrouping;
}

export interface InvoiceRequestParams {
  limit: number;
  offset: number;
  order: 'asc' | 'desc';
  filter: 'active' | 'deleted';
  projectIds: Array<number>;
  sort: InvoiceListFields;
}
